import { LogOut } from 'lucide-react'
import { Logo } from './Logo'
import { useMemo, useState, type ReactNode } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import AppearancePopover from '@/components/appearance/AppearancePopover'
import BottomNav from '@/components/BottomNav'
import WallpaperLayer from '@/components/appearance/WallpaperLayer'
import { Button } from '@/components/ui/button'
import { BOTTOM_NAV_PATHS, filterNavForUser } from '@/config/nav'
import { ROLE_LABELS, type Role } from '@/config/roles'
import { useAuth } from '@/lib/auth'

function isNavActive(pathname: string, to: string, allPaths: string[]) {
  const matches = allPaths.filter((p) => pathname === p || pathname.startsWith(`${p}/`))
  if (matches.length === 0) return false
  const best = matches.reduce((a, b) => (a.length >= b.length ? a : b))
  return best === to
}

export default function Layout({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [open, setOpen] = useState(false)

  const visible = useMemo(() => filterNavForUser(user), [user])
  const allPaths = useMemo(() => visible.map((n) => n.to), [visible])

  const bottomItems = useMemo(() => {
    const byPath = new Map(visible.map((n) => [n.to, n]))
    return BOTTOM_NAV_PATHS.map((p) => byPath.get(p)).filter(Boolean) as typeof visible
  }, [visible])

  const moreActive = useMemo(() => {
    if (!visible.length) return false
    const onPrimary = BOTTOM_NAV_PATHS.some((p) => isNavActive(location.pathname, p, [...BOTTOM_NAV_PATHS]))
    if (onPrimary) return false
    return visible.some(
      (n) => !(BOTTOM_NAV_PATHS as readonly string[]).includes(n.to) && isNavActive(location.pathname, n.to, allPaths),
    )
  }, [visible, location.pathname, allPaths])

  if (!user) return null

  return (
    <div className="app-shell flex min-h-screen">
      <WallpaperLayer />
      <div
        className="fixed inset-x-3 top-3 z-40 flex h-14 items-center rounded-2xl px-4 lg:hidden"
        style={{ background: 'var(--surface)', border: '1px solid var(--legacy-border)', boxShadow: 'var(--shadow-soft)' }}
      >
        <div className="flex min-w-0 items-center gap-2">
          <Logo className="h-9" />
          <span className="font-heading truncate text-sm font-semibold">Admin</span>
        </div>
      </div>

      <aside
        data-testid="sidebar"
        className={`fixed top-0 left-0 z-50 flex h-[100dvh] w-72 flex-col overflow-hidden transition-transform lg:sticky lg:transform-none ${
          open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div
          className="m-0 flex h-full flex-col overflow-hidden rounded-none lg:m-4 lg:h-[calc(100dvh-2rem)] lg:rounded-2xl"
          style={{ background: 'var(--surface)', border: '1px solid var(--legacy-border)', boxShadow: 'var(--shadow-soft)' }}
        >
          <div className="flex flex-col items-start gap-1 px-6 pt-6 pb-4">
            <Logo className="h-14" />
            <div className="text-xs font-medium tracking-wide uppercase" style={{ color: 'var(--text-muted)' }}>
              Admin Toko
            </div>
          </div>
          <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto p-3">
            {visible.map((n) => {
              const Icon = n.icon
              const active = isNavActive(location.pathname, n.to, allPaths)
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.to.replace(/\//g, '-')}`}
                  onClick={() => setOpen(false)}
                  className={`side-link ${active ? 'active' : ''}`}
                >
                  <span className="nav-ico">
                    <Icon className="size-4.5" strokeWidth={active ? 2.5 : 2} />
                  </span>
                  <span>{n.label}</span>
                </NavLink>
              )
            })}
          </nav>
          <div className="shrink-0 p-4">
            <AppearancePopover triggerClassName="mb-3 w-full justify-start gap-2" align="start" />
            <div className="mb-3 flex items-center gap-3">
              <div
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full font-heading text-sm font-semibold text-white"
                style={{ background: 'var(--primary)' }}
              >
                {user.nama?.[0]?.toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold">{user.nama}</div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {ROLE_LABELS[user.role as Role] ?? user.role}
                </div>
              </div>
            </div>
            <Button data-testid="logout-btn" onClick={async () => {
                await signOut()
                navigate('/login', { replace: true })
              }} variant="outline" size="sm" className="w-full">
              <LogOut className="size-4" /> Keluar
            </Button>
          </div>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-40 bg-black/20 lg:hidden" onClick={() => setOpen(false)} />}
      <main className="min-w-0 flex-1 pt-20 pb-24 lg:pt-0 lg:pb-0">
        <div className="fade-in mx-auto max-w-[1400px] p-4 sm:p-6 lg:p-8">{children}</div>
      </main>

      <BottomNav items={bottomItems} onOpenMore={() => setOpen(true)} moreActive={moreActive} />
    </div>
  )
}

export { navFor } from '@/config/nav'
